import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import {
  SpraySession,
  SprayProgramRef,
  PpeChecklistItem,
} from '../../services/sprayService';
import { getSessionWithCacheFallback } from '../../services/spraySessionCacheService';
import { start as startSpraySession } from '../../services/spraySessionStartService';
import {
  readDraft,
  writeDraft,
  clearDraft,
} from '../../services/sprayCompletionDraft';
import OpenSignOffWeatherPanel, {
  SprayWeatherSnapshotInput,
} from './OpenSignOffWeatherPanel';

/**
 * Format a Date as the value expected by an HTML <input type="datetime-local">,
 * which is "YYYY-MM-DDTHH:MM" in the user's local timezone.
 */
function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

const nowAsDatetimeLocal = (): string => toDatetimeLocalValue(new Date());

interface Props {
  sessionId: string;
  /**
   * Fires once Start Session succeeds, with the session as it now stands
   * (status "in_progress" + startDate stamped). The parent landing uses
   * this to immediately swap to FieldModeCompletionForm so the operator
   * is never stranded on an "already started — Complete Session" CTA
   * whose Link points to the URL they're already on (a router no-op).
   *
   * Also fires when the operator taps the [Complete Session] CTA on the
   * fallback "already started" branch — a defensive path that only
   * renders if pre-flight is mounted standalone with a started session.
   *
   * Track: spray_weather_enhancements_20260429 / Phase 3.
   */
  onStarted?: (session: SpraySession) => void;
}

/**
 * FieldModePreFlightForm — pre-flight screen mounted at
 * /spray/sessions/:id/complete when the session has not yet been started.
 *
 * Operator flow:
 *   1. Tick every PPE item.
 *   2. Confirm/adjust the Start time (defaults to "now", capped at "now").
 *   3. Tap Start Session — POSTs PPE + startedAt to /sessions/:id/start.
 *
 * If the operator lands here on an already-started session (startDate set),
 * the Start button is replaced with a Complete Session call-to-action.
 * In production this branch is unreachable because the parent landing
 * routes started sessions directly to FieldModeCompletionForm; it is
 * preserved as a defensive fallback for tests that mount the pre-flight
 * standalone.
 *
 * Track: spray_session_workflow_20260427 / Phase 3 / Task 3.
 *        spray_weather_enhancements_20260429 / Phase 3.
 */
const FieldModePreFlightForm: React.FC<Props> = ({ sessionId, onStarted }) => {
  const { t } = useTranslation();

  const [session, setSession] = useState<SpraySession | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const [ppe, setPpe] = useState<PpeChecklistItem[]>([]);
  const [startTime, setStartTime] = useState<string>(nowAsDatetimeLocal());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Start time tracks "now" until either (a) the operator manually
  // overrides it or (b) the session is started — at which point the
  // moment of /start is the source of truth. The auto-refresh mirrors
  // the End-time treatment on the completion form: the common flow is
  // "I'm starting right now", and the operator should not have to
  // re-enter the time if the form has been open for a while.
  const startTimeUserEdited = useRef(false);

  // Latest weather snapshot collected by the OpenSignOffWeatherPanel —
  // shape matches sprayWeatherService.captureSnapshot's input. Held in a
  // ref so updates from the panel do not cascade re-renders into the rest
  // of the pre-flight form. Wiring this into the /sessions/:id/start
  // payload is tracked separately; for now the panel's job is to collect
  // and display it.
  const weatherSnapshotRef = useRef<SprayWeatherSnapshotInput | null>(null);

  // The datetime-local max is computed once on mount so it doesn't drift
  // in tests. The backend's sanity bound is the source of truth; this is
  // a UX hint for the operator.
  const nowMax = useMemo(() => nowAsDatetimeLocal(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    getSessionWithCacheFallback(sessionId)
      .then(({ session: loaded }) => {
        if (cancelled) return;
        setSession(loaded);
        // Layer the saved draft on top of the session-supplied PPE
        // template — checked-state from the draft wins, but a v2 draft
        // missing a newly-introduced PPE item still gets rendered as
        // unchecked from the template.
        const draft = readDraft(sessionId);
        const template = (loaded.ppeChecklist || []).map(p => ({ ...p }));
        if (draft?.ppe?.length) {
          const draftMap = new Map(draft.ppe.map(p => [p.item, p.checked]));
          for (const entry of template) {
            const checked = draftMap.get(entry.item);
            if (checked !== undefined) entry.checked = checked;
          }
        }
        setPpe(template);
        // Start time is intentionally NOT restored from the draft —
        // see startTimeUserEdited / auto-refresh comments. Reseeding to
        // current time on every mount avoids resurrecting a stale value.
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadError(err?.message || 'Failed to load session.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Persist the draft on every meaningful field change. The completion
  // form will read this same key — DRAFT_VERSION 2 is shared across
  // both screens (Phase 4 / Task 7). startTime is deliberately NOT
  // persisted: the operator's mental model is "I'm starting now", and
  // the auto-refresh effect below keeps the field current — restoring
  // a stale value across remounts would defeat that.
  useEffect(() => {
    if (loading || !session || session.startDate) return;
    // Carry over any keys the completion form may have already written
    // (endTime, wind, temperature, quantities) so this write doesn't
    // overwrite them.
    const existing = readDraft(sessionId);
    writeDraft(sessionId, {
      ...(existing ?? {}),
      ppe,
    });
  }, [sessionId, ppe, loading, session]);

  // Start time auto-refresh: while the operator has the pre-flight open
  // and has NOT manually edited the Start time, keep the field current
  // so a quick tap on Start always logs the actual moment. The interval
  // disengages once the session is started — at that point the moment
  // of /start is the source of truth and the form will swap into the
  // already-started branch (no Start time field to update anyway).
  useEffect(() => {
    if (loading || !session || session.startDate) return;
    const id = setInterval(() => {
      if (startTimeUserEdited.current) return;
      setStartTime(nowAsDatetimeLocal());
    }, 30 * 1000);
    return () => clearInterval(id);
  }, [loading, session]);

  const togglePpe = (index: number) => {
    setPpe(prev =>
      prev.map((entry, i) =>
        i === index ? { ...entry, checked: !entry.checked } : entry
      )
    );
  };

  const allPpeChecked = useMemo(
    () => ppe.length > 0 && ppe.every(p => p.checked),
    [ppe]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!session) return;
    if (!allPpeChecked) {
      setFormError(t('spray.sessions.preFlight.allPpeRequired'));
      return;
    }
    if (!startTime) {
      setFormError(t('spray.sessions.preFlight.startTimeRequired'));
      return;
    }
    const startMs = Date.parse(startTime);
    if (Number.isNaN(startMs)) {
      setFormError(t('spray.sessions.preFlight.startTimeInvalid'));
      return;
    }
    setSubmitting(true);
    const startedAt = new Date(startMs).toISOString();
    try {
      // Offline-aware Start: spraySessionStartService.start() posts
      // /start when online, queues + flips the local cache optimistically
      // when the network is unreachable. Either path resolves with the
      // session locally marked in_progress at `startedAt`, so we can
      // immediately re-render into the completion form without a
      // network re-fetch.
      await startSpraySession(sessionId, {
        ppeChecklist: ppe,
        startedAt,
      });
      // The Start payload landed (or queued); the pre-flight-side draft
      // fields (PPE + startTime) are no longer useful. The open sign-off
      // panel's start-weather fields, however, MUST survive — the
      // close sign-off form reads them at /complete time to build
      // weatherSnapshots.start. Drop the pre-flight-only keys but keep
      // the start-weather fields the panel persisted.
      // Track: spray_weather_enhancements_20260429 / Phase 5 wiring fix
      // (round 2). The previous unconditional clearDraft() call here was
      // why the export's start snapshot landed empty in production.
      const existing = readDraft(sessionId);
      if (existing && (
        existing.startTemperatureCategory ||
        existing.startWindCategory ||
        existing.startPrecipitationCategory ||
        existing.startWeatherReading
      )) {
        writeDraft(sessionId, {
          startTemperatureCategory: existing.startTemperatureCategory,
          startWindCategory: existing.startWindCategory,
          startPrecipitationCategory: existing.startPrecipitationCategory,
          startWeatherReading: existing.startWeatherReading,
        });
      } else {
        clearDraft(sessionId);
      }
      // Locally flip the rendered session so the parent landing
      // component swaps in the completion form. No re-fetch — works
      // offline and matches the optimistic cache flip.
      const updatedSession = session
        ? { ...session, status: 'in_progress' as const, startDate: startedAt }
        : null;
      setSession(updatedSession);
      // Tell the parent landing the session is now started. The landing's
      // FieldModeFormRouter uses this to update its own session state and
      // swap the rendered form to FieldModeCompletionForm — without this
      // notification the operator would land on the (broken) "already
      // started — Complete Session" CTA whose Link points at the URL
      // they're already on.
      if (onStarted && updatedSession) {
        onStarted(updatedSession);
      }
    } catch (err: any) {
      setFormError(err?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="field-mode-preflight-form text-center p-4">
        <Spinner animation="border" role="status" />
        <div className="mt-2">{t('common.loading')}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="field-mode-preflight-form p-3">
        <Alert variant="danger">{loadError}</Alert>
      </div>
    );
  }

  if (!session) return null;

  const programName =
    typeof session.programId === 'object' && session.programId
      ? (session.programId as SprayProgramRef).name
      : '';

  // Defensive fallback: pre-flight rendered with an already-started
  // session. In production the landing routes such sessions straight to
  // FieldModeCompletionForm so this branch never paints; the existing
  // "already started → Complete Session" tests render pre-flight
  // standalone and exercise this path. Tapping the CTA fires onStarted
  // so the parent (when present) can swap to the close sign-off form;
  // a Link here would re-target the URL the operator is already on,
  // which is a router no-op.
  if (session.startDate) {
    const handleCompleteClick = () => {
      if (onStarted) onStarted(session);
    };
    return (
      <div className="field-mode-preflight-form p-3">
        <Card className="mb-3">
          <Card.Body>
            <Card.Title className="mb-1">{programName || session.name}</Card.Title>
            {session.name && programName && (
              <Card.Subtitle className="text-muted">{session.name}</Card.Subtitle>
            )}
            <p className="text-muted mb-0 mt-2">{t('spray.sessions.preFlight.alreadyStarted')}</p>
          </Card.Body>
        </Card>
        <div className="d-grid">
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleCompleteClick}
          >
            {t('spray.sessions.preFlight.completeSession')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form
      className="field-mode-preflight-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="mb-1">{programName || session.name}</Card.Title>
          {session.name && programName && (
            <Card.Subtitle className="text-muted">{session.name}</Card.Subtitle>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Card.Title>{t('spray.sessions.preFlight.ppeChecklistTitle')}</Card.Title>
          <div className="d-flex flex-column gap-2 mt-2">
            {ppe.map((entry, idx) => (
              <Form.Check
                key={`${entry.item}-${idx}`}
                type="checkbox"
                id={`preflight-ppe-${idx}`}
                label={entry.item}
                checked={entry.checked}
                onChange={() => togglePpe(idx)}
                className="fs-5"
              />
            ))}
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Card.Title>{t('spray.sessions.preFlight.startTimeSection')}</Card.Title>
          <Form.Group controlId="preflight-start-time" className="mt-2">
            <Form.Label>{t('spray.sessions.preFlight.startTime')}</Form.Label>
            <Form.Control
              type="datetime-local"
              value={startTime}
              max={nowMax}
              onChange={e => {
                // Mark as user-overridden so the auto-refresh stops.
                startTimeUserEdited.current = true;
                setStartTime(e.target.value);
              }}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <OpenSignOffWeatherPanel
        sessionId={sessionId}
        startTime={startTime}
        onChange={snapshot => {
          weatherSnapshotRef.current = snapshot;
        }}
      />

      {formError && <Alert variant="danger">{formError}</Alert>}

      <div className="d-grid">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={submitting || !allPpeChecked}
        >
          {submitting
            ? t('spray.sessions.preFlight.starting')
            : t('spray.sessions.preFlight.startSession')}
        </Button>
      </div>
    </Form>
  );
};

export default FieldModePreFlightForm;
